import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast'; // 🔥 IMPORT DU TOAST
import { 
  User, 
  Mail, 
  Phone, 
  Building, 
  MapPin, 
  ArrowRight
} from 'lucide-react';

// Fonction utilitaire pour formater les erreurs d'API
const formatApiError = (error, errorData = null) => {
  // Log détaillé pour les développeurs
  console.error('🔍 Erreur API détaillée:', error);
  console.error('📊 Données de réponse:', error.response?.data);
  console.error('📊 ErrorData:', errorData);
  console.error('🌐 Statut HTTP:', error.response?.status);
  
  // Utiliser errorData si disponible, sinon error.response?.data
  const responseData = errorData || error.response?.data;
  
  if (responseData) {
    
    // Erreur de duplication de téléphone (gestion spécifique du format de l'erreur)
    if (responseData.error && (
      (responseData.error.includes('Duplicate entry') && responseData.error.includes('phone')) ||
      (responseData.error.includes('1062') && responseData.error.includes('phone'))
    )) {
      console.error('📱 Erreur de duplication de téléphone détectée:', responseData.error);
      return 'Ce numéro de téléphone est déjà utilisé. Veuillez utiliser un autre numéro.';
    }
    
    // Erreur de duplication d'email
    if (responseData.error && (
      (responseData.error.includes('Duplicate entry') && responseData.error.includes('email')) ||
      (responseData.error.includes('1062') && responseData.error.includes('email'))
    )) {
      console.error('📧 Erreur de duplication d\'email détectée:', responseData.error);
      return 'Cette adresse email est déjà utilisée. Veuillez utiliser une autre adresse email.';
    }
    
    // Erreur de duplication de nom d'utilisateur
    if (responseData.error && (
      (responseData.error.includes('Duplicate entry') && responseData.error.includes('username')) ||
      (responseData.error.includes('1062') && responseData.error.includes('username'))
    )) {
      console.error('👤 Erreur de duplication de nom d\'utilisateur détectée:', responseData.error);
      return 'Ce nom d\'utilisateur est déjà utilisé. Veuillez choisir un autre nom d\'utilisateur.';
    }
    
    // Erreur de validation générale
    if (responseData.error && typeof responseData.error === 'string') {
      console.error('⚠️ Erreur de validation:', responseData.error);
      // Si c'est une erreur technique, retourner un message générique
      if (responseData.error.includes('1062') || responseData.error.includes('Duplicate entry')) {
        return 'Les informations fournies sont déjà utilisées. Veuillez vérifier vos données.';
      }
      return responseData.error;
    }
    
    // Erreur avec message détaillé
    if (responseData.message) {
      console.error('💬 Message d\'erreur:', responseData.message);
      return responseData.message;
    }
    
    // Erreur avec détails
    if (responseData.detail) {
      console.error('📋 Détails de l\'erreur:', responseData.detail);
      return responseData.detail;
    }
  }
  
  // Erreur de réseau
  if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
    console.error('🌐 Erreur de réseau détectée');
    return 'Erreur de connexion. Vérifiez votre connexion internet et réessayez.';
  }
  
  // Erreur de timeout
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    console.error('⏰ Timeout détecté');
    return 'La requête a pris trop de temps. Veuillez réessayer.';
  }
  
  // Erreur par défaut
  console.error('❓ Erreur non gérée:', error.message);
  return 'Une erreur inattendue s\'est produite. Veuillez réessayer.';
};

export default function Register() {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    username: '',
    email: '',
    phone: '',
    countryCode: '+225', // Code pays par défaut (Côte d'Ivoire)
    role: 'entreprise', // Valeur par défaut : entreprise
    statut: 'actif', // Sera mis à jour selon le rôle
    business_type: 'expediteur', // Valeur par défaut : expediteur
    business_type_other: '',
    address: '',
    transporteur: '1' // ID du transporteur par défaut
  });
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Liste des codes pays les plus courants
  const countryCodes = [
    { code: '+225', country: 'Côte d\'Ivoire', flag: '🇨🇮' },
    { code: '+33', country: 'France', flag: '🇫🇷' },
    { code: '+1', country: 'États-Unis/Canada', flag: '🇺🇸' },
    { code: '+44', country: 'Royaume-Uni', flag: '🇬🇧' },
    { code: '+49', country: 'Allemagne', flag: '🇩🇪' },
    { code: '+39', country: 'Italie', flag: '🇮🇹' },
    { code: '+34', country: 'Espagne', flag: '🇪🇸' },
    { code: '+32', country: 'Belgique', flag: '🇧🇪' },
    { code: '+41', country: 'Suisse', flag: '🇨🇭' },
    { code: '+212', country: 'Maroc', flag: '🇲🇦' },
    { code: '+213', country: 'Algérie', flag: '🇩🇿' },
    { code: '+216', country: 'Tunisie', flag: '🇹🇳' },
    { code: '+221', country: 'Sénégal', flag: '🇸🇳' },
    { code: '+223', country: 'Mali', flag: '🇲🇱' },
    { code: '+226', country: 'Burkina Faso', flag: '🇧🇫' },
    { code: '+227', country: 'Niger', flag: '🇳🇪' },
    { code: '+228', country: 'Togo', flag: '🇹🇬' },
    { code: '+229', country: 'Bénin', flag: '🇧🇯' },
    { code: '+230', country: 'Maurice', flag: '🇲🇺' },
    { code: '+231', country: 'Liberia', flag: '🇱🇷' },
    { code: '+232', country: 'Sierra Leone', flag: '🇸🇱' },
    { code: '+233', country: 'Ghana', flag: '🇬🇭' },
    { code: '+234', country: 'Nigeria', flag: '🇳🇬' },
    { code: '+235', country: 'Tchad', flag: '🇹🇩' },
    { code: '+236', country: 'République centrafricaine', flag: '🇨🇫' },
    { code: '+237', country: 'Cameroun', flag: '🇨🇲' },
    { code: '+238', country: 'Cap-Vert', flag: '🇨🇻' },
    { code: '+239', country: 'Sao Tomé-et-Principe', flag: '🇸🇹' },
    { code: '+240', country: 'Guinée équatoriale', flag: '🇬🇶' },
    { code: '+241', country: 'Gabon', flag: '🇬🇦' },
    { code: '+242', country: 'République du Congo', flag: '🇨🇬' },
    { code: '+243', country: 'République démocratique du Congo', flag: '🇨🇩' },
    { code: '+244', country: 'Angola', flag: '🇦🇴' },
    { code: '+245', country: 'Guinée-Bissau', flag: '🇬🇼' },
    { code: '+246', country: 'Territoire britannique de l\'océan Indien', flag: '🇮🇴' },
    { code: '+247', country: 'Ascension', flag: '🇦🇨' },
    { code: '+248', country: 'Seychelles', flag: '🇸🇨' },
    { code: '+249', country: 'Soudan', flag: '🇸🇩' },
    { code: '+250', country: 'Rwanda', flag: '🇷🇼' },
    { code: '+251', country: 'Éthiopie', flag: '🇪🇹' },
    { code: '+252', country: 'Somalie', flag: '🇸🇴' },
    { code: '+253', country: 'Djibouti', flag: '🇩🇯' },
    { code: '+254', country: 'Kenya', flag: '🇰🇪' },
    { code: '+255', country: 'Tanzanie', flag: '🇹🇿' },
    { code: '+256', country: 'Ouganda', flag: '🇺🇬' },
    { code: '+257', country: 'Burundi', flag: '🇧🇮' },
    { code: '+258', country: 'Mozambique', flag: '🇲🇿' },
    { code: '+260', country: 'Zambie', flag: '🇿🇲' },
    { code: '+261', country: 'Madagascar', flag: '🇲🇬' },
    { code: '+262', country: 'Réunion', flag: '🇷🇪' },
    { code: '+263', country: 'Zimbabwe', flag: '🇿🇼' },
    { code: '+264', country: 'Namibie', flag: '🇳🇦' },
    { code: '+265', country: 'Malawi', flag: '🇲🇼' },
    { code: '+266', country: 'Lesotho', flag: '🇱🇸' },
    { code: '+267', country: 'Botswana', flag: '🇧🇼' },
    { code: '+268', country: 'Eswatini', flag: '🇸🇿' },
    { code: '+269', country: 'Comores', flag: '🇰🇲' },
    { code: '+290', country: 'Sainte-Hélène', flag: '🇸🇭' },
    { code: '+291', country: 'Érythrée', flag: '🇪🇷' },
    { code: '+297', country: 'Aruba', flag: '🇦🇼' },
    { code: '+298', country: 'Îles Féroé', flag: '🇫🇴' },
    { code: '+299', country: 'Groenland', flag: '🇬🇱' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Si le rôle change, mettre à jour le statut automatiquement
    if (name === 'role') {
      let newStatut = 'inactif';
      if (value === 'relay_point') {
        newStatut = 'inactif';
      } else if (value === 'entreprise' || value === 'client') {
        newStatut = 'actif';
      }
      
      setFormData({
        ...formData,
        [name]: value,
        statut: newStatut
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const validateForm = () => {
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.phone) {
      toast.error('Veuillez remplir tous les champs obligatoires.', { duration: 5000 });
      return false;
    }
    if (formData.role === 'relay_point' || formData.role === 'entreprise') {
      if (!formData.business_type) {
        toast.error('Veuillez sélectionner le type d\'entreprise.', { duration: 5000 });
        return false;
      }
      if (formData.business_type === 'other' && !formData.business_type_other.trim()) {
        toast.error('Veuillez préciser le type d\'entreprise.', { duration: 5000 });
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (!validateForm()) {
      setIsLoading(false);
      return;
    }

    try {
      const userData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        username: formData.username || formData.email, // Utilise l'email comme username si non fourni
        email: formData.email,
        phone: `${formData.countryCode}${formData.phone}`, // Combine le code pays et le numéro
        role: formData.role,
        statut:formData.statut,
        business_type: formData.business_type,
        address: formData.address,
        transporteur: formData.transporteur // ID du transporteur
      };

      // const response = await userAPI.createUser(userData);
      const response = await authAPI.register(userData);
      // const response = await fetch('https://backend.katianlogistique.com/api/auth/register/', {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //   },
      //   body: JSON.stringify(userData)
      // });

      console.log('✅ Inscription réussie:', response);
      
      // Gestion des messages et redirections selon le rôle
      if (formData.role === 'client') {
        toast.success('Compte créé avec succès ! Redirection vers votre espace client...', { duration: 3000 });
        
        // Rediriger vers l'espace business après 2 secondes
        setTimeout(() => {
          window.location.href = 'https://business.katianlogistique.com';
        }, 2000);
      } else if (formData.role === 'entreprise' || formData.role === 'relay_point') {
        toast.success('Compte créé avec succès ! Notre équipe vous contactera dans les 48h pour finaliser votre inscription.', { duration: 4000 });
        
        // Rediriger vers la page d'accueil après 3 secondes
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        toast.success('Compte créé avec succès ! Redirection vers votre espace...', { duration: 3000 });
        
        // Rediriger vers la page d'accueil après 2 secondes
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    } catch (error) {
      console.error('❌ Erreur d\'inscription:', error);
      
      // Extraire errorData si disponible
      let errorData = null;
      if (error.response?.data) {
        errorData = error.response.data;
      }
      
      const errorMessage = formatApiError(error, errorData);
      toast.error(errorMessage, { duration: 8000 });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-dark-bg dark:to-dark-bg-secondary py-8 sm:py-12">
      <div className="container-ksl px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
              Créer votre compte expediteur ou client
            </h1>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300">
              Rejoignez Katian et commencez à expedier plus facilement
            </p>
          </div>


          {/* Form */}
          <div className="bg-white dark:bg-dark-bg-secondary rounded-2xl shadow-lg p-4 sm:p-6 md:p-8 border border-gray-200 dark:border-gray-700">

            <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
              {/* Formulaire en une seule étape */}
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6 border-b border-gray-200 dark:border-gray-700 pb-2">
                  Informations d'inscription
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    {/* Prénom */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Prénom <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="text"
                          name="first_name"
                          value={formData.first_name}
                          onChange={handleChange}
                          placeholder="Votre prénom"
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-ksl-red"
                          required
                        />
                      </div>
                    </div>

                    {/* Nom */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Nom <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="text"
                          name="last_name"
                          value={formData.last_name}
                          onChange={handleChange}
                          placeholder="Votre nom"
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-ksl-red"
                          required
                        />
                      </div>
                    </div>

                    {/* Username */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Nom d'utilisateur <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="text"
                          name="username"
                          value={formData.username}
                          onChange={handleChange}
                          placeholder="nom_utilisateur"
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-ksl-red"
                          required
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="email@exemple.com"
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-ksl-red"
                          required
                        />
                      </div>
                    </div>

                    {/* Téléphone - S'étend sur toute la largeur */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Téléphone <span className="text-red-500">*</span>
                      </label>
                      <div className="flex space-x-2">
                        {/* Sélecteur de code pays */}
                        <div className="relative w-32 flex-shrink-0">
                          <select
                            name="countryCode"
                            value={formData.countryCode}
                            onChange={handleChange}
                            className="w-full pl-3 pr-8 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-ksl-red appearance-none"
                            required
                          >
                            {countryCodes.map((country) => (
                              <option key={country.code} value={country.code}>
                                {country.flag} {country.code}
                              </option>
                            ))}
                          </select>
                          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                        
                        {/* Champ numéro de téléphone - Occupe toute la largeur restante */}
                        <div className="relative flex-1 min-w-0">
                          <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="XX XX XX XX XX"
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-ksl-red"
                            required
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Numéro complet: {formData.countryCode} {formData.phone || 'XX XX XX XX XX'}
                      </p>
                    </div>

                    {/* Rôle - Masqué, valeur par défaut: entreprise */}
                    {/* <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Type de compte <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <select
                          name="role"
                          value={formData.role}
                          onChange={handleChange}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-ksl-red"
                          required
                        >
                          <option value="client">Particulier</option>
                          <option value="entreprise">Entreprise</option>
                        </select>
                      </div>
                    </div> */}
                  
                  {/* Type d'entreprise - Masqué, valeur par défaut: expediteur */}
                  {/* {(formData.role === 'relay_point' || formData.role === 'entreprise') && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Type d'entreprise <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <select
                          name="business_type"
                          value={formData.business_type}
                          onChange={handleChange}
                          className="w-full pl-10 pr-4 py-2 sm:py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-ksl-red"
                          required={formData.role === 'relay_point' || formData.role === 'entreprise'}
                        >
                          <option value="">Sélectionner le type d'entreprise</option>
                          <option value="expediteur">Expediteur</option>
                          <option value="pharmacy">Pharmacie</option>
                          <option value="grocery">Épicerie</option>
                          <option value="gas_station">Station essence</option>
                          <option value="cyber_cafe">Cyber café</option>
                          <option value="bookstore">Librairie</option>
                          <option value="other">Autre</option>
                        </select>
                      </div>
                    </div>
                  )} */}

                  {(formData.role === 'relay_point' || formData.role === 'entreprise') && formData.business_type === 'other' && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Précisez votre type d'entreprise <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="text"
                          name="business_type_other"
                          value={formData.business_type_other}
                          onChange={handleChange}
                          placeholder="Ex: Atelier de réparation, Coopérative, etc."
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-ksl-red"
                          required
                        />
                      </div>
                    </div>
                  )}

                  {/* Adresse */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Adresse
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="Adresse complète"
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-ksl-red"
                      />
                    </div>
                  </div>
                </div>

                {/* Bouton de soumission */}
                <div className="text-center mt-6 sm:mt-8">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-4 sm:px-6 md:px-8 py-3 sm:py-4 bg-ksl-red text-white rounded-xl hover:bg-ksl-red-dark transition-all duration-300 font-medium flex items-center justify-center space-x-2 disabled:opacity-50 text-sm sm:text-base md:text-lg mx-auto"
                  >
                    {isLoading ? (
                      <>
                        <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-sm sm:text-base">Création...</span>
                      </>
                    ) : (
                      <>
                        <span className="text-sm sm:text-base">Créer mon compte</span>
                        <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* Login Link */}
          <div className="text-center mt-6 sm:mt-8">
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
              Vous avez déjà un compte ?{' '}
              <a 
                  href="https://business.katianlogistique.com" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary transition-colors duration-200 text-center whitespace-nowrap"
                  
                >
                  Se connecter
                </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
} 